import axiosInstance from './axios';
import { PaginatedResponse, User } from '../../types';

export interface Permission {
  id: number;
  code: string;
  name: string;
  module: string;
  action: 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'export' | 'import' | 'manage';
  description?: string;
  createdAt: string;
}

export interface PermissionGroup {
  id: number;
  code: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  permissions?: Permission[];
}

export interface CreatePermissionGroupDto {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
  permissionIds?: number[];
}

export interface UpdatePermissionGroupDto {
  code?: string;
  name?: string;
  description?: string;
  isActive?: boolean;
  permissionIds?: number[];
}

export interface PermissionGroupFilters {
  page?: number;
  pageSize?: number;
  q?: string;
  isActive?: boolean;
}

class PermissionGroupsService {
  private basePath = '/permission-groups';

  /**
   * Get all permission groups with pagination
   */
  async getAll(filters?: PermissionGroupFilters): Promise<PaginatedResponse<PermissionGroup>> {
    const response = await axiosInstance.get<PaginatedResponse<PermissionGroup>>(this.basePath, {
      params: filters,
    });
    return response.data;
  }

  /**
   * Get a single permission group by ID
   */
  async getById(id: number): Promise<PermissionGroup> {
    const response = await axiosInstance.get<PermissionGroup>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new permission group
   */
  async create(data: CreatePermissionGroupDto): Promise<PermissionGroup> {
    const response = await axiosInstance.post<PermissionGroup>(this.basePath, data);
    return response.data;
  }

  /**
   * Update a permission group
   */
  async update(id: number, data: UpdatePermissionGroupDto): Promise<PermissionGroup> {
    const response = await axiosInstance.patch<PermissionGroup>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a permission group
   */
  async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${this.basePath}/${id}`);
  }

  /**
   * Get all available permissions
   */
  async getAllPermissions(): Promise<Permission[]> {
    const response = await axiosInstance.get<Permission[]>(`${this.basePath}/permissions`);
    return response.data;
  }

  /**
   * Get permissions grouped by module
   */
  async getPermissionsByModule(): Promise<Record<string, Permission[]>> {
    const response = await axiosInstance.get<Record<string, Permission[]>>(
      `${this.basePath}/permissions/by-module`
    );
    return response.data;
  }

  /**
   * Get current user's permissions
   */
  async getMyPermissions(): Promise<string[]> {
    const response = await axiosInstance.get<string[]>(`${this.basePath}/my-permissions`);
    return response.data;
  }

  /**
   * Get users in a group
   */
  async getGroupUsers(groupId: number): Promise<User[]> {
    const response = await axiosInstance.get<User[]>(`${this.basePath}/${groupId}/users`);
    return response.data;
  }

  /**
   * Assign users to a group
   */
  async assignUsers(groupId: number, userIds: number[]): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `${this.basePath}/${groupId}/users`,
      { userIds }
    );
    return response.data;
  }

  /**
   * Remove users from a group
   */
  async removeUsers(groupId: number, userIds: number[]): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `${this.basePath}/${groupId}/users`,
      { data: { userIds } }
    );
    return response.data;
  }

  /**
   * Get permissions for a specific user
   */
  async getUserPermissions(userId: number): Promise<string[]> {
    const response = await axiosInstance.get<string[]>(
      `${this.basePath}/users/${userId}/permissions`
    );
    return response.data;
  }

  /**
   * Get groups for a specific user
   */
  async getUserGroups(userId: number): Promise<PermissionGroup[]> {
    const response = await axiosInstance.get<PermissionGroup[]>(
      `${this.basePath}/users/${userId}/groups`
    );
    return response.data;
  }
}

export const permissionGroupsService = new PermissionGroupsService();
